import classNames from "classnames";
import { FieldAttributes } from "formik";
import React from "react";
import styled from "styled-components";
import { Icon } from "./Icon";
import { BaseInput, ErrorText, FocusOutline, InputProps, InputWrapper, Label, RequiredAsterisk, Wrapper } from "./TextInputUtils";
import { Tooltip } from "./Tooltip";
import { useFirstRequiredErrorInForm } from "./hooks/useFirstRequiredErrorInForm";
import { style } from "./style";

const Wrap = styled(InputWrapper)`
  max-width: 1000px;
  min-width: 300px;
`;

const Input = styled(BaseInput)`
  && {
    min-height: 96px;
    max-height: 264px;
    resize: none;
    border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.dark};
    padding: ${style.vars.space["3"]};
    color: ${style.colors.grey.darkest};
    width: 100%;
    height: 100%;
    z-index: ${style.vars.zIndex.base};
  }
`;

const ErrorIconWrapper = styled.div`
  position: absolute;
  z-index: ${style.vars.zIndex.base};
  right: ${style.vars.space["3"]};
  top: ${style.vars.space["3"]};
`;

/**
 * @deprecated Use `TextInputV2.TextArea` instead.
 */
export const TextArea: React.FC<InputProps | FieldAttributes<any>> = ({
  required = false,
  disabled = false,
  mono = false,
  name,
  label,
  labelTooltip,
  value,
  placeholder,
  error,
  className,
  touched,
  description,
  ...inputProps
}) => {
  const ref = React.useRef<HTMLInputElement | null>(null);
  const { hasRequiredError, onInput } = useFirstRequiredErrorInForm(ref);

  const hasPropError = error && touched;
  const isInvalid = hasPropError || hasRequiredError;

  const classes = classNames({
    [className]: className,
    "is-invalid": isInvalid,
    "is-required": required,
  });

  return (
    <Wrapper className={classes} data-testid="textarea">
      <Label htmlFor={name}>
        {label}
        {required && <RequiredAsterisk>*</RequiredAsterisk>}
        {labelTooltip && <Tooltip.Help content={labelTooltip} />}
      </Label>
      {description && <div>{description}</div>}
      <Wrap>
        <Input
          component="textarea"
          id={name}
          name={name}
          label={label}
          value={value}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          $mono={mono}
          innerRef={ref}
          onInput={onInput}
          {...inputProps}
        />
        <FocusOutline />
        {hasPropError && (
          <ErrorIconWrapper>
            <Icon large icon="error" />
          </ErrorIconWrapper>
        )}
      </Wrap>
      {hasPropError || hasRequiredError ? (
        <ErrorText className="required-err">{hasPropError ? error : label + " is required"}</ErrorText>
      ) : null}
    </Wrapper>
  );
};
